import React, { useEffect, useState } from 'react';
import { FaEye, FaPen, FaKey } from 'react-icons/fa';
import { getUsers, getUsersStats, createUser, updateUser, toggleUserStatus, resetPassword, getStations } from '../services/api';

const ROLE_LABELS: Record<string, string> = {
  superadmin: 'Super Admin', admin_siege: 'Admin Siege', comptable: 'Comptable',
  financier: 'Financier', fiscaliste: 'Fiscaliste', gerant: 'Gerant',
  pompiste: 'Pompiste', auditeur: 'Auditeur'
};

const EMPTY_FORM = { nom: '', prenom: '', email: '', telephone: '', role: 'pompiste', station_id: '', password: '' };

const Users: React.FC = () => {
  const [users, setUsers] = useState<any[]>([]);
  const [stations, setStations] = useState<any[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [roleFilter, setRoleFilter] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showPwdModal, setShowPwdModal] = useState(false);
  const [editingUser, setEditingUser] = useState<any>(null);
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [form, setForm] = useState<any>({ ...EMPTY_FORM });
  const [newPassword, setNewPassword] = useState('');
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');

  useEffect(() => { loadAll(); }, []);
  useEffect(() => { loadUsers(); }, [roleFilter]);

  const loadAll = async () => {
    try {
      const [sRes, stRes] = await Promise.all([getUsersStats(), getStations()]);
      setStats(sRes.data);
      setStations(stRes.data);
    } catch (err) { console.error(err); }
    loadUsers();
  };

  const loadUsers = async () => {
    setLoading(true);
    try {
      const params: any = {};
      if (roleFilter) params.role = roleFilter;
      if (search) params.search = search;
      const res = await getUsers(params);
      setUsers(res.data);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const handleToggle = async (id: number) => {
    try {
      await toggleUserStatus(id);
      loadUsers();
    } catch (err) { console.error(err); }
  };

  const openCreateModal = () => {
    setEditingUser(null);
    setForm({ ...EMPTY_FORM });
    setMsg(''); setError('');
    setShowModal(true);
  };

  const openEditModal = (u: any) => {
    setEditingUser(u);
    setForm({ nom: u.nom, prenom: u.prenom, email: u.email, telephone: u.telephone || '', role: u.role, station_id: u.station_id || '', password: '' });
    setMsg(''); setError('');
    setShowModal(true);
  };

  const handleSave = async () => {
    setMsg(''); setError('');
    try {
      if (editingUser) {
        await updateUser(editingUser.id, { nom: form.nom, prenom: form.prenom, email: form.email, telephone: form.telephone, role: form.role, station_id: form.station_id || null });
        setMsg('Utilisateur mis a jour');
      } else {
        await createUser({ ...form, station_id: form.station_id || null, password: form.password || undefined });
        setMsg('Utilisateur cree');
      }
      setShowModal(false);
      loadUsers();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erreur');
    }
  };

  const handleResetPwd = async () => {
    if (!selectedUser || !newPassword) return;
    try {
      await resetPassword(selectedUser.id, { password: newPassword });
      setMsg('Mot de passe reinitialise');
      setShowPwdModal(false);
      setNewPassword('');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erreur');
    }
  };

  const openDetail = (u: any) => { setSelectedUser(u); setShowDetailModal(true); };
  const openPwdModal = (u: any) => { setSelectedUser(u); setNewPassword(''); setShowPwdModal(true); };

  return (
    <div>
      <div className="page-header">
        <h2>Utilisateurs</h2>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <span className="badge">{users.length}</span>
          <button className="btn btn-primary" onClick={openCreateModal}>+ Nouvel utilisateur</button>
        </div>
      </div>

      {msg && <div className="alert alert-success">{msg}</div>}
      {error && <div className="alert alert-error">{error}</div>}

      {/* Stats */}
      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10, marginBottom: 20 }}>
          <div className="card" style={{ padding: 14, textAlign: 'center' }}>
            <div style={{ fontSize: 11, color: '#888' }}>ACTIFS</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: '#004A97' }}>{stats.total}</div>
          </div>
          {stats.parRole?.slice(0, 4).map((r: any) => (
            <div key={r.role} className="card" style={{ padding: 14, textAlign: 'center' }}>
              <div style={{ fontSize: 11, color: '#888' }}>{ROLE_LABELS[r.role] || r.role}</div>
              <div style={{ fontSize: 22, fontWeight: 700 }}>{r.total}</div>
            </div>
          ))}
        </div>
      )}

      <div className="filters">
        <select value={roleFilter} onChange={e => setRoleFilter(e.target.value)}>
          <option value="">Tous les roles</option>
          {Object.entries(ROLE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher..." onKeyDown={e => e.key === 'Enter' && loadUsers()} />
      </div>

      {loading ? <div className="loading">Chargement...</div> : (
        <table className="table">
          <thead>
            <tr><th>Nom</th><th>Email</th><th>Telephone</th><th>Role</th><th>Station</th><th>Statut</th><th>Derniere connexion</th><th>Actions</th></tr>
          </thead>
          <tbody>
            {users.map((u: any) => (
              <tr key={u.id}>
                <td><strong>{u.prenom} {u.nom}</strong></td>
                <td>{u.email}</td>
                <td>{u.telephone || '-'}</td>
                <td><span className="badge badge-info">{ROLE_LABELS[u.role] || u.role}</span></td>
                <td>{u.station_nom || '-'}</td>
                <td><span className={`badge badge-${u.etat ? 'success' : 'critical'}`}>{u.etat ? 'Actif' : 'Inactif'}</span></td>
                <td>{u.last_login ? new Date(u.last_login).toLocaleDateString('fr-FR') : '-'}</td>
                <td>
                  <div style={{ display: 'flex', gap: 4 }}>
                    <button className="btn btn-sm btn-secondary" onClick={() => openDetail(u)} title="Details"><FaEye /></button>
                    <button className="btn btn-sm btn-primary" onClick={() => openEditModal(u)} title="Modifier"><FaPen /></button>
                    <button className="btn btn-sm btn-warning" onClick={() => openPwdModal(u)} title="Reinitialiser MDP"><FaKey /></button>
                    <button className={`btn btn-sm ${u.etat ? 'btn-danger' : 'btn-success'}`} onClick={() => handleToggle(u.id)}>
                      {u.etat ? 'Desactiver' : 'Activer'}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 550 }}>
            <div className="modal-header">
              <h3>{editingUser ? 'Modifier l\'utilisateur' : 'Nouvel utilisateur'}</h3>
              <button className="modal-close" onClick={() => setShowModal(false)}>&times;</button>
            </div>
            <div className="modal-body">
              {error && <div className="alert alert-error">{error}</div>}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="form-group">
                  <label>Prenom *</label>
                  <input type="text" value={form.prenom} onChange={e => setForm({...form, prenom: e.target.value})} />
                </div>
                <div className="form-group">
                  <label>Nom *</label>
                  <input type="text" value={form.nom} onChange={e => setForm({...form, nom: e.target.value})} />
                </div>
                <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                  <label>Email *</label>
                  <input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} />
                </div>
                <div className="form-group">
                  <label>Telephone</label>
                  <input type="text" value={form.telephone} onChange={e => setForm({...form, telephone: e.target.value})} />
                </div>
                <div className="form-group">
                  <label>Role *</label>
                  <select value={form.role} onChange={e => setForm({...form, role: e.target.value})}>
                    {Object.entries(ROLE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </div>
                <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                  <label>Station</label>
                  <select value={form.station_id} onChange={e => setForm({...form, station_id: e.target.value})}>
                    <option value="">-- Aucune (siege) --</option>
                    {stations.map((s: any) => <option key={s.id} value={s.id}>{s.nom}</option>)}
                  </select>
                </div>
                {!editingUser && (
                  <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                    <label>Mot de passe (defaut: Pass@2026)</label>
                    <input type="password" value={form.password} onChange={e => setForm({...form, password: e.target.value})} placeholder="Pass@2026" />
                  </div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Annuler</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={!form.nom || !form.prenom || !form.email}>
                {editingUser ? 'Mettre a jour' : 'Creer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedUser && (
        <div className="modal-overlay" onClick={() => setShowDetailModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 500 }}>
            <div className="modal-header">
              <h3>Details utilisateur</h3>
              <button className="modal-close" onClick={() => setShowDetailModal(false)}>&times;</button>
            </div>
            <div className="modal-body" style={{ lineHeight: 2 }}>
              <div><strong>Nom complet:</strong> {selectedUser.prenom} {selectedUser.nom}</div>
              <div><strong>Email:</strong> {selectedUser.email}</div>
              <div><strong>Telephone:</strong> {selectedUser.telephone || '-'}</div>
              <div><strong>Role:</strong> <span className="badge badge-info">{ROLE_LABELS[selectedUser.role] || selectedUser.role}</span></div>
              <div><strong>Station:</strong> {selectedUser.station_nom || 'Siege'}</div>
              <div><strong>Statut:</strong> <span className={`badge badge-${selectedUser.etat ? 'success' : 'critical'}`}>{selectedUser.etat ? 'Actif' : 'Inactif'}</span></div>
              <div><strong>Derniere connexion:</strong> {selectedUser.last_login ? new Date(selectedUser.last_login).toLocaleString('fr-FR') : 'Jamais'}</div>
              <div><strong>Cree le:</strong> {new Date(selectedUser.created_at).toLocaleDateString('fr-FR')}</div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-primary" onClick={() => { setShowDetailModal(false); openEditModal(selectedUser); }}>Modifier</button>
              <button className="btn btn-secondary" onClick={() => setShowDetailModal(false)}>Fermer</button>
            </div>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {showPwdModal && selectedUser && (
        <div className="modal-overlay" onClick={() => setShowPwdModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 400 }}>
            <div className="modal-header">
              <h3>Reinitialiser le mot de passe</h3>
              <button className="modal-close" onClick={() => setShowPwdModal(false)}>&times;</button>
            </div>
            <div className="modal-body">
              <p>Utilisateur: <strong>{selectedUser.prenom} {selectedUser.nom}</strong></p>
              <div className="form-group">
                <label>Nouveau mot de passe</label>
                <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="Nouveau mot de passe" />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowPwdModal(false)}>Annuler</button>
              <button className="btn btn-warning" onClick={handleResetPwd} disabled={!newPassword}>Reinitialiser</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;
